import React from 'react'
import { useTranslation } from 'react-i18next'
import { sdk } from '@/lib/sdk'
import { cn } from '@/lib/utils'
import { imageUrl } from '@/lib/images'
import { countryFlag, countryName } from '@/lib/memberPage'
import { unavailableKindOf, type UnavailableKind } from '@/components/shared/itemUnavailableKind'
import type { MemberPageData } from '@/types/memberPage'

/**
 * F-177: loads the member page as the viewer may see it. Signed in, the
 * community route (`GET /members/{id}`, filtered per viewer); signed out, the
 * public route, whose 404 means "no page for you" whatever the reason.
 */
export const useMemberPage = (memberId: string | null, { signedOut, enabled = true }: { signedOut: boolean; enabled?: boolean }) => {
  const [member, setMember] = React.useState<MemberPageData | null>(null)
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<UnavailableKind | null>(null)

  const load = React.useCallback(async (quiet = false) => {
    if (!memberId || !enabled) return
    if (!quiet) setLoading(true)
    setError(null)
    try {
      const loaded = signedOut ? fromPublic(await sdk.members.getPublic(memberId)) : (await sdk.members.get(memberId)) as MemberPageData | null
      if (loaded) setMember(loaded)
      else setError('missing')
    } catch (err) {
      setMember(null)
      setError(unavailableKindOf(err))
    } finally {
      setLoading(false)
    }
  }, [memberId, signedOut, enabled])

  React.useEffect(() => {
    if (!enabled) { setMember(null); setError(null); setLoading(false); return }
    void load()
  }, [load, enabled])

  return { member, setMember, loading, error, reload: load }
}

/** The public page has its own keys (`name`, `memberSince`, no role): read like the community one. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- the public payload, read key by key
const fromPublic = (data: any): MemberPageData | null => {
  if (!data) return null
  return {
    ...data,
    fullName: data.name ?? null,
    joinedAt: data.memberSince ?? data.communityStats?.memberSince ?? '',
    lastSeenAt: null,
    role: data.role ?? '',
  } as MemberPageData
}

export const shownNameOf = (member?: Pick<MemberPageData, 'fullName' | 'name' | 'displayName'> | null): string =>
  member?.fullName || member?.name || member?.displayName || ''

/** The person's cover, or the community's gradient (as before F-177) when there is none. */
export const MemberCover = ({ url, className }: { url?: string | null; className?: string }) => (
  url
    ? <img src={imageUrl(url, { w: 1800 }) || url} alt="" className={cn('w-full object-cover', className)} data-member-cover />
    : <div className={cn('w-full bg-gradient-to-r from-brand-500 to-brand-600', className)} data-member-cover-gradient />
)

/** The name with the country's flag, when the viewer may see the country. */
export const NameWithFlag = ({ name, country, as: Tag = 'h2', className }: { name: string; country?: string | null; as?: 'h1' | 'h2'; className?: string }) => {
  const { i18n } = useTranslation()
  const flag = countryFlag(country)
  return (
    <Tag className={cn('min-w-0 break-words font-bold text-gray-900 [overflow-wrap:anywhere]', className)}>
      {name}
      {flag && (
        <span className="ml-2 align-middle text-[0.8em]" role="img" aria-label={countryName(country as string, i18n.language)} title={countryName(country as string, i18n.language)} data-member-flag>
          {flag}
        </span>
      )}
    </Tag>
  )
}

/** "{cargo} na {empresa}", or whichever of the two the person filled in. */
export const Headline = ({ jobTitle, company, className }: { jobTitle?: string | null; company?: string | null; className?: string }) => {
  const { t } = useTranslation('members')
  const text = jobTitle && company ? t('page.headline', { title: jobTitle, company }) : jobTitle || company
  if (!text) return null
  return <p className={cn('break-words text-base font-medium text-gray-700 [overflow-wrap:anywhere]', className)} data-member-headline>{text}</p>
}

export const RoleChip = ({ role, className }: { role?: string | null; className?: string }) => {
  const { t } = useTranslation('members')
  if (!role) return null
  return (
    <span className={cn('inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700', className)} data-member-role>
      {t(`role.${role}`, { defaultValue: role })}
    </span>
  )
}
